import {
  FilterAction,
  FilterKeys,
  VehicleFilter,
} from "@modules/vehicle/types/vehicleFilter";
import { CloseIcon } from "@talladega/icons";
import { useEffect, useState } from "react";
import { Button } from "@talladega/components";
import VehicleYearFilter from "@modules/vehicle/components/VehicleYearFilter";
import VehicleMileageFilter from "@modules/vehicle/components/VehicleMileageFilter";
import VehicleBadgeContainer from "@modules/vehicle/components/VehicleBadgeContainer";
import { useTranslation } from "next-i18next";
import useGetVehicleFilterData from "@modules/vehicle/hooks/useVehicleFilterData";
import VehicleFiltersSkeleton from "@common/components/loader/VehicleFiltersSkeleton";
import VehicleFeatureFilter from "./VehicleFeatureFilter";
import VehicleMakeModelFilter from "./VehicleMakeModelFilter";
import VehicleTransmissionFilter from "./VehicleTransmissionFilter";
import VehicleBodyStyleFilter from "./VehicleBodyStylesFilter";
import SavedVehiclesFilter from "./SavedVehiclesFilter";
import { UserFavouriteVehicle } from "../types/UserFavouriteVehicle";
import VehicleExteriorColourFilter from "./VehicleExteriorColourFilter";
import VehicleFuelEfficiencyFilter from "./VehicleFuelEfficiencyFilter";
import VehicleSortFilter from "./VehicleSortFilter";
import VehiclePricingFilter from "./VehiclePricingFilter";
import VehicleLocationFilter from "./VehicleLocationFilter";
import { PaymentFactors } from "../hooks/useVehicleList";
import canVehicleFilterBeBadged from "../helpers/canVehicleFilterBeBadged";
import VehicleWarrantyFilter from "./VehicleWarrantyFilter";
import VehiclePassengerCapacityFilter from "./VehiclePassengerCapacityFilter";

const vehicleLocationFilterOn = false;

type VehicleFilterProps = {
  totalVehicles: number | undefined;
  onHideClick(): void;
  vehicleFilters: VehicleFilter[];
  onFilterUpdate(filterAction: FilterAction): void;
  favouriteVehicles?: UserFavouriteVehicle[];
  onSaveSearchClick(): void;
  locale?: string;
  paymentFactors?: PaymentFactors;
  expandPricingFilter?: boolean;
};

const VehicleFilterContainer = ({
  totalVehicles,
  onHideClick,
  onFilterUpdate,
  vehicleFilters,
  favouriteVehicles = [],
  onSaveSearchClick,
  locale = "en",
  paymentFactors,
  expandPricingFilter = false,
}: VehicleFilterProps) => {
  const { t } = useTranslation(["vehiclesFilters"]);
  const { vehicleFilters: vehicleFiltersData, isLoading: isFiltersLoading } =
    useGetVehicleFilterData();

  // Height of desktop filter div is dynamic based on scroll position between the top of page and when the div sticks
  const pageAtTopOffset = 204;
  const pageAtSidebarStickyOffset = 36;
  const [filterDivOffsetY, setFilterDivOffsetY] = useState(pageAtTopOffset);
  useEffect(() => {
    const onScroll = () =>
      setFilterDivOffsetY(
        Math.max(pageAtTopOffset - window.scrollY, pageAtSidebarStickyOffset)
      );
    window.removeEventListener("scroll", onScroll);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const canShowSavedSearchOption =
    vehicleFilters.filter((vf) => canVehicleFilterBeBadged(vf)).length > 0 &&
    !vehicleFilters.some((vf) => vf.key === FilterKeys.SavedVehicles);

  return (
    <form
      style={
        {
          "--filterDivOffsetY": `${filterDivOffsetY}px`,
        } as React.CSSProperties
      }
      className="w-full md:ml-0 flex flex-col h-[calc(100vh-80px)] lg:h-[calc(100vh-var(--filterDivOffsetY))]"
      action="#"
      method="GET"
      onSubmit={(e) => e.preventDefault()}
    >
      {isFiltersLoading || vehicleFiltersData === null ? (
        <VehicleFiltersSkeleton numberOfItems={10} />
      ) : (
        <>
          <div className="pt-5 pb-4 pl-4 pr-4 sm:pl-6 lg:pt-1 flex flex-row items-center justify-between border-b border-gray-50 lg:border-0">
            <p className="text-text-light-200 body-4 leading-6 font-semibold tracking-widest">
              {t("vehiclesFilters:filter_and_sort").toUpperCase()}
            </p>
            <CloseIcon
              className="lg:hidden w-6 h-6 text-gray-500 cursor-pointer"
              onClick={onHideClick}
            />
          </div>
          <div className="lg:hidden xs:inline">
            <VehicleBadgeContainer
              onFilterUpdate={onFilterUpdate}
              vehicleFilters={vehicleFilters}
              scrollable
              reverse
            />
          </div>
          <div className="flex-grow pb-20">
            <div className="py-4 px-4 sm:px-6 w-full bg-primary-light/50 text-primary-bold-600 border-b border-gray-50 caption-1 leading-5">
              {canShowSavedSearchOption && (
                <div className="flex lg:block flex-nowrap items-center gap-6">
                  <div>{t("vehiclesFilters:search_banner_filter_select")}</div>
                  <div className="flex-shrink-0 lg:pt-2">
                    <Button
                      fill="outline"
                      spacing="tight-hug"
                      buttonStyle="transparent"
                      border="thick"
                      size="small"
                      onClick={() => onSaveSearchClick()}
                    >
                      <span className="hidden lg:inline font-medium">
                        {t("vehiclesFilters:save_your_search")}
                      </span>
                      <span className="inline lg:hidden font-medium">
                        {t("vehiclesFilters:save_search")}
                      </span>
                    </Button>
                  </div>
                </div>
              )}
              {!canShowSavedSearchOption && (
                <span>{t("vehiclesFilters:search_banner")}</span>
              )}
            </div>
            <VehicleSortFilter
              onUpdate={onFilterUpdate}
              sortByData={vehicleFiltersData.sortBy}
              vehicleFilters={vehicleFilters}
            />
            {favouriteVehicles.length > 0 && (
              <SavedVehiclesFilter
                vehicleFilters={vehicleFilters}
                onUpdate={onFilterUpdate}
              />
            )}
            {vehicleLocationFilterOn && (
              <VehicleLocationFilter
                vehicleFilters={vehicleFilters}
                provincesAndLocations={vehicleFiltersData.provincesAndLocations}
                onUpdate={onFilterUpdate}
              />
            )}
            <VehicleMakeModelFilter
              vehicleFilters={vehicleFilters}
              onUpdate={onFilterUpdate}
              makesModels={vehicleFiltersData.makesAndModels}
            />
            <VehiclePricingFilter
              vehicleFilters={vehicleFilters}
              onUpdate={onFilterUpdate}
              pricing={vehicleFiltersData.pricing}
              paymentFactors={paymentFactors}
              defaultOpen={expandPricingFilter}
            />
            <VehicleWarrantyFilter
              vehicleFilters={vehicleFilters}
              onUpdate={onFilterUpdate}
            />
            <VehicleBodyStyleFilter
              vehicleFilters={vehicleFilters}
              bodyStyles={vehicleFiltersData.bodyStyles}
              onUpdate={onFilterUpdate}
            />
            <VehicleFuelEfficiencyFilter
              vehicleFilters={vehicleFilters}
              onUpdate={onFilterUpdate}
              fuelTypes={vehicleFiltersData.fuelTypes}
            />
            <VehicleYearFilter
              vehicleFilters={vehicleFilters}
              onUpdate={onFilterUpdate}
              defaultMin={vehicleFiltersData.years.min}
              defaultMax={vehicleFiltersData.years.max}
            />
            <VehicleMileageFilter
              vehicleFilters={vehicleFilters}
              onUpdate={onFilterUpdate}
              defaultMin={vehicleFiltersData.mileage.min}
              defaultMax={vehicleFiltersData.mileage.max}
              locale={locale}
            />
            <VehicleFeatureFilter
              vehicleFilters={vehicleFilters}
              featureTypes={vehicleFiltersData.featureTypes}
              onUpdate={onFilterUpdate}
            />
            <VehiclePassengerCapacityFilter
              vehicleFilters={vehicleFilters}
              passengerCapacities={vehicleFiltersData.passengerCapacities}
              onUpdate={onFilterUpdate}
            />
            <VehicleExteriorColourFilter
              vehicleFilters={vehicleFilters}
              exteriorColours={vehicleFiltersData.exteriorColours}
              onUpdate={onFilterUpdate}
            />
            <VehicleTransmissionFilter
              vehicleFilters={vehicleFilters}
              transmissionTypes={vehicleFiltersData.transmissionTypes}
              onUpdate={onFilterUpdate}
            />
          </div>

          <div className="bg-white items-center py-6 justify-center h-20 xs:flex lg:hidden body-3 border-t border-gray-50 fixed inset-x-0 bottom-0 z-[5]">
            <Button onClick={onHideClick} spacing="hug">
              <span>
                {t("vehiclesFilters:filter_show_x_results", {
                  count: totalVehicles,
                })}
              </span>
            </Button>
          </div>
        </>
      )}
    </form>
  );
};

export default VehicleFilterContainer;
